import { type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';
import { routes } from '@tig-dpqa-cloud/contract-backend-frontend';
import { findTrackedContractRoute, getTrackedContractRoutes } from '../request-activity-route-matcher';
import { LoadingQueueService } from '../services/loading-queue.service';
import { SKIP_REQUEST_ACTIVITY } from './request-activity.context';

const trackedRoutes = getTrackedContractRoutes(routes);

let requestCounter = 0;

/**
 * Check if a request (path + method) matches any of the tracked contract routes.
 */
const isTrackedRoute = (url: string, method: string): boolean => {
  try {
    return !!findTrackedContractRoute(url, method, trackedRoutes);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[RequestActivityInterceptor] Error checking if route is tracked:', error);
    return false;
  }
};

const generateRequestId = (req: HttpRequest<unknown>): string => {
  requestCounter += 1;
  return `${Date.now()}-${requestCounter}-${req.method}-${req.urlWithParams}`;
};

export const shouldTrackRequest = (req: HttpRequest<unknown>): boolean => {
  if (req.context.get(SKIP_REQUEST_ACTIVITY)) {
    return false;
  }

  return isTrackedRoute(req.url, req.method);
};

export const requestActivityInterceptor: HttpInterceptorFn = (req, next) => {
  try {
    if (!shouldTrackRequest(req)) {
      return next(req);
    }

    const loadingQueue = inject(LoadingQueueService);
    const requestId = generateRequestId(req);
    loadingQueue.start(requestId, req);

    return next(req).pipe(
      finalize(() => {
        loadingQueue.end(requestId);
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[RequestActivityInterceptor] Unexpected error:', error);
    return next(req);
  }
};
